import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Clock, ChefHat, Check, Truck, X,
  ChevronLeft, ChevronRight, RefreshCw, Volume2, VolumeX, User, CalendarClock,
} from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { sortActiveOrdersByUrgency } from "@/utils/sortOrders";
import { formatScheduledDateForKDS } from "@/utils/orderDateFormatting";

// Kitchen only sees: Aguardando → Produzindo → Listo
// "Enviado" and "Entregue" are managed by the attendant in Orders page
// Orders in "Listo" auto-hide after this many minutes to prevent accumulation.
// Staff can still confirm delivery earlier via the "Entregar" button.
const LISTO_AUTO_HIDE_MINS = 20;

const KITCHEN_COLUMNS = [
  { key: "pending", label: "Aguardando", icon: Clock, color: "bg-amber-500" },
  { key: "preparing", label: "Produzindo", icon: ChefHat, color: "bg-blue-500" },
  { key: "ready", label: "Listo", icon: Check, color: "bg-emerald-500" },
];

export default function KitchenMonitorPage() {
  const { authAxios, token } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const heartbeatRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const soundEnabledRef = useRef(true);
  const audioCtxRef = useRef(null);
  const ordersRef = useRef([]);
  const pendingUpdatesRef = useRef(new Map());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === "suspended") {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener("click", unlock, { once: true });
    document.addEventListener("touchstart", unlock, { once: true });
    return () => {
      document.removeEventListener("click", unlock);
      document.removeEventListener("touchstart", unlock);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabledRef.current) return;
    try {
      if (audioCtxRef.current && audioCtxRef.current.state === "running") {
        const ctx = audioCtxRef.current;
        [[880, 0, 0.15], [1100, 0.18, 0.35]].forEach(([freq, start, end]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
          gain.gain.setValueAtTime(0.45, ctx.currentTime + start);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + end);
          osc.start(ctx.currentTime + start);
          osc.stop(ctx.currentTime + end);
        });
        return;
      }
      const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleZNkMk55ruTYxppxUENrmtfz0K96X0dci8Xg2r6agGNQW5bE5eDHp4dqU1h0m8Lf2selgGBWYYW73efYxpx4YF5qhLHX3tfHoHxqZG2LstXd08OngnBla4ax0dzTyaeCbmlzk7zT3dPGonlpbnKXvtPc0caieW1wdZm/0tzPw516bXV5nMHT3M/Dn3tudnqew9Pd0MOee292e6DD093Rwp14b3h9o8TT3dHBnHhveH6kxdPd0cGbd294f6XG1N3RwJp3b3l/pcbU3dHAmXZven+mx9Td0L+ZdnB6gKfI1N3Qvph1cHqBqMnU3dC+l3Vwe4KpydTdz72WdHF7g6rK1d3PvJV0cXuEq8rV3c+7lHNxe4Wsy9Xdz7qTc3F8hq3L1d3OuZJycXyHrszW3c64kXJyfYiuzNbdzreQcnJ9ia/N1t3Oto9xcn6Jr87X3c61jnFyfoquz9fdzLSOcXN+iq/Q19zLs41xc3+Lr9HY3MqyjHBzf4yw0djcyrGLcHOAjbHS2NzJsIpwdICOstPZ3MiviXB0gY+z1NncyK6JcHSBkLTV2dvHrYhwdYKRtdba28athXB1gpK22tvbxayEcHaCk7fa29vEq4NwdoOUuNvb28OrgnB2g5W43Nvaw6qBcHeDlrnc29rCqYBwd4SXut3b2sGof3B4hJi73tvZwKd+cHiEmrzf29nAp31weYWbvd/b2b+mfHB5hZy+4NvYvqV7cHmGnb/g29i9pHpweoadwOHb2Lyjend6h5/B4drYvKJ5d3qIoMLh2te7oXh3e4mhw+Ha1rqgd3d7iqLD4trWuZ93d3uLo8Tj2tW4nnZ3fIykxeTa1bedddg=");
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (e) {}
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrders = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/orders?hidden=false&limit=200");
      const allOrders = res.data || [];
      const now = Date.now();
      const ordersWithOverrides = allOrders.map(o => {
        const optimistic = pendingUpdatesRef.current.get(o.id);
        return optimistic ? { ...o, status: optimistic } : o;
      });
      const kitchenOrders = ordersWithOverrides.filter(o => {
        if (o.hidden === true) return false;
        if (!["pending", "preparing", "ready", "scheduled"].includes(o.status)) return false;
        // Incident replay guard: unpaid orders with no scheduled_for older than 8h are stale.
        // Exempt: paid orders, orders with scheduled_for (agendados), status=scheduled.
        const isPaid = o.payment_status === "paid";
        const isScheduledOrder = o.status === "scheduled" || !!o.scheduled_for;
        if (!isPaid && !isScheduledOrder) {
          const createdAt = new Date(o.created_at);
          if ((now - createdAt.getTime()) / 60000 > 480) return false;
        }
        if (o.status === "ready") {
          const updatedAt = new Date(o.updated_at || o.created_at);
          const minsReady = (now - updatedAt.getTime()) / 60000;
          if (minsReady > LISTO_AUTO_HIDE_MINS) {
            console.info("[KDS_REMOVED_FROM_BOARD]", {
              order_id: o.id,
              reason: "ready_auto_hide",
              hidden_reason: "frontend_ttl",
              mins_ready: Math.floor(minsReady),
              ttl_mins: LISTO_AUTO_HIDE_MINS,
            });
            return false;
          }
        }
        return true;
      });
      const newPending = kitchenOrders.filter(o => o.status === "pending");
      const prevPendingIds = new Set(ordersRef.current.filter(o => o.status === "pending").map(o => o.id));
      const genuinelyNew = newPending.filter(o => !prevPendingIds.has(o.id));
      if (genuinelyNew.length > 0) playNotificationSound();
      const pending = pendingUpdatesRef.current;
      const merged = pending.size === 0 ? kitchenOrders : kitchenOrders
        .filter(o => !(pending.has(o.id) && pending.get(o.id) === "delivered"))
        .map(o => pending.has(o.id) ? { ...o, status: pending.get(o.id) } : o);
      ordersRef.current = merged;
      setOrders(merged);
      setLastUpdate(new Date());
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
    setLoading(false);
  }, [playNotificationSound]);

  useEffect(() => {
    fetchOrders();
    let wsCleanedUp = false;
    const API = API_URL || "";
    const restaurantId = (() => {
      try {
        const t = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        if (!t) return null;
        return JSON.parse(atob(t.split(".")[1])).restaurant_id || null;
      } catch { return null; }
    })();
    if (!restaurantId) {
      console.error("[KDS] restaurant_id ausente no token — WebSocket não conectado");
      setLoading(false);
      toast.error("Sessão inválida. Por favor recarregue a página ou faça login novamente.");
      return;
    }
    const stopHeartbeat = () => {
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
    };
    const refreshAccessToken = async () => {
      const refreshTok = localStorage.getItem("refresh_token");
      if (!refreshTok) {
        return localStorage.getItem("token") || sessionStorage.getItem("token") || token || "";
      }
      try {
        const res = await authAxiosRef.current.post("/auth/refresh", { refresh_token: refreshTok });
        const next = res.data?.access_token;
        if (next) {
          localStorage.setItem("token", next);
          if (res.data?.refresh_token) {
            localStorage.setItem("refresh_token", res.data.refresh_token);
          }
          return next;
        }
      } catch (err) {
        console.warn("[KDS_WS] token refresh failed:", err?.response?.status || err?.message);
      }
      return localStorage.getItem("token") || sessionStorage.getItem("token") || token || "";
    };

    const connectWs = async () => {
      if (wsCleanedUp) return;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
      const freshToken = await refreshAccessToken();
      let freshRestaurantId = restaurantId;
      try {
        if (freshToken) {
          freshRestaurantId = JSON.parse(atob(freshToken.split(".")[1])).restaurant_id || restaurantId;
        }
      } catch { /* keep initial restaurantId */ }
      if (!freshRestaurantId || !freshToken) {
        console.error("[KDS] token ou restaurant_id ausente — WebSocket não conectado");
        return;
      }
      const wsUrl = API.replace(/^http/, "ws") + "/api/ws/kds/" + freshRestaurantId + "?token=" + encodeURIComponent(freshToken);
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => {
          setWsConnected(true);
          reconnectAttemptRef.current = 0;
          fetchOrders();
          stopHeartbeat();
          heartbeatRef.current = setInterval(() => {
            if (ws.readyState !== WebSocket.OPEN) return;
            try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
            pongTimeoutRef.current = setTimeout(() => {
              try { ws.close(); } catch {};
            }, 5000);
          }, 25000);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
            if (data.type === "pong") return;
            console.log("[KDS_WS] event received:", data.event || data.type, data.order?.id || "");
            if (data.event === "new_order" && data.order) {
              const newOrder = data.order;
              const _wsNow = Date.now();
              const _isAutoHidden = newOrder.status === "ready" &&
                ((_wsNow - new Date(newOrder.updated_at || newOrder.created_at).getTime()) / 60000 > LISTO_AUTO_HIDE_MINS);
              if (!_isAutoHidden && ["pending", "preparing", "ready", "scheduled"].includes(newOrder.status)) {
                if (newOrder.hidden === true) return;
                setOrders(prev => {
                  const exists = prev.some(o => o.id === newOrder.id);
                  const next = exists
                    ? prev.map(o => o.id === newOrder.id ? newOrder : o)
                    : [newOrder, ...prev];
                  ordersRef.current = next;
                  return next;
                });
              }
              if (newOrder.status === "pending") playNotificationSound();
            } else if (data.event === "order_updated" && data.order) {
              const updatedOrder = data.order;
              pendingUpdatesRef.current.delete(updatedOrder.id);
              const kdsStatuses = ["pending", "preparing", "ready", "scheduled"];
              const _wsNow2 = Date.now();
              const _readyAutoHidden = updatedOrder.status === "ready" &&
                ((_wsNow2 - new Date(updatedOrder.updated_at || updatedOrder.created_at).getTime()) / 60000 > LISTO_AUTO_HIDE_MINS);
              setOrders(prev => {
                const shouldShow = kdsStatuses.includes(updatedOrder.status) && !_readyAutoHidden && updatedOrder.hidden !== true;
                const next = shouldShow
                  ? prev.map(o => o.id === updatedOrder.id ? updatedOrder : o)
                  : prev.filter(o => o.id !== updatedOrder.id);
                ordersRef.current = next;
                return next;
              });
            } else if (data.event === "catch_up" && Array.isArray(data.orders)) {
              const kdsStatuses = ["pending", "preparing", "ready", "scheduled"];
              const _now = Date.now();
              setOrders(prev => {
                const existingIds = new Set(prev.map(o => o.id));
                const toAdd = data.orders.filter(o => {
                  if (existingIds.has(o.id)) return false;
                  if (o.hidden === true) return false;
                  if (!kdsStatuses.includes(o.status)) return false;
                  if (o.status === "ready") {
                    const updatedAt = new Date(o.updated_at || o.created_at);
                    if ((_now - updatedAt.getTime()) / 60000 > LISTO_AUTO_HIDE_MINS) return false;
                  }
                  return true;
                });
                if (toAdd.length === 0) return prev;
                const next = [...prev, ...toAdd];
                ordersRef.current = next;
                const newPending = toAdd.filter(o => o.status === "pending");
                if (newPending.length > 0) playNotificationSound();
                return next;
              });
            }
          } catch { /* ignore parse errors */ }
        };
        ws.onclose = () => {
          stopHeartbeat();
          setWsConnected(false);
          if (!wsCleanedUp) {
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            reconnectAttemptRef.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current - 1), 30000);
            reconnectRef.current = setTimeout(() => { connectWs(); }, delay);
          }
        };
        ws.onerror = () => { ws.close(); };
      } catch {
        setWsConnected(false);
        if (!wsCleanedUp) {
          reconnectAttemptRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current - 1), 30000);
          reconnectRef.current = setTimeout(() => { connectWs(); }, delay);
        }
      }
    };
    connectWs();
    const interval = setInterval(() => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) fetchOrders();
    }, 3000);
    const safetyInterval = setInterval(() => fetchOrders(), 15000);
    const reminderInterval = setInterval(() => {
      if (ordersRef.current.some(o => o.status === "pending")) {
        playNotificationSound();
      }
    }, 60000);
    return () => {
      wsCleanedUp = true;
      clearInterval(interval);
      clearInterval(safetyInterval);
      clearInterval(reminderInterval);
      stopHeartbeat();
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, []);

  const updateStatus = async (orderId, status) => {
    pendingUpdatesRef.current.set(orderId, status);
    setOrders(prev => {
      const kdsStatuses = ["pending", "preparing", "ready"];
      const next = kdsStatuses.includes(status)
        ? prev.map(o => o.id === orderId ? { ...o, status } : o)
        : prev.filter(o => o.id !== orderId);
      ordersRef.current = next;
      return next;
    });
    pendingUpdatesRef.current.set(orderId, status);
    try {
      await authAxios.patch(`/orders/${orderId}/status?status=${status}`);
      pendingUpdatesRef.current.delete(orderId);
      const msgs = { preparing: "Produzindo", ready: "Listo", pending: "Retornado", delivered: "Entregue ✓" };
      toast.success(msgs[status] || "Atualizado");
      if (status === "delivered") {
        const order = ordersRef.current.find(o => o.id === orderId);
        const isMesa = order?.table_number && !_isBalcao(order?.table_name);
        if (!isMesa) authAxios.patch(`/orders/${orderId}/hide`).catch(() => {});
        pendingUpdatesRef.current.delete(orderId);
      }
    } catch (err) {
      pendingUpdatesRef.current.delete(orderId);
      if (err?.response?.status === 409) {
        toast.warning("Outro utilizador já atualizou este pedido. Atualizando dados...");
      } else {
        toast.error("Erro ao atualizar — a reverter");
      }
      fetchOrders();
    }
  };

  const cancelOrder = async (orderId, orderCode) => {
    if (!window.confirm(`Cancelar pedido #${orderCode}? Esta ação não pode ser desfeita.`)) return;
    setOrders(prev => {
      const next = prev.filter(o => o.id !== orderId);
      ordersRef.current = next;
      return next;
    });
    try {
      await authAxios.patch(`/orders/${orderId}/status?status=cancelled`);
      toast.success(`Pedido #${orderCode} cancelado`);
    } catch {
      toast.error("Erro ao cancelar — a reverter");
      fetchOrders();
    }
  };

  const getElapsed = (createdAt) => Math.floor((currentTime - new Date(createdAt)) / 60000);

  const _isBalcao = (name) => /balc[aã]o/i.test(name || "");

  const getOrderType = (order) => {
    if (order.source === "counter" || order.source === "balcao" || _isBalcao(order.table_name))
      return { label: "Balcão", badge: "bg-yellow-100 text-yellow-700 border-yellow-200" };
    if (order.table_number) return { label: "Mesa", badge: "bg-orange-100 text-orange-700 border-orange-200" };
    if (order.delivery_address || order.source === "delivery") return { label: "Delivery", badge: "bg-blue-100 text-blue-700 border-blue-200" };
    return { label: "Retirada", badge: "bg-purple-100 text-purple-700 border-purple-200" };
  };

  const canAdvance = (order, currentStatus) => {
    if (order.status === "awaiting_payment") return false;
    const isDelivery = order.delivery_address || order.source === "delivery";
    if (isDelivery && currentStatus === "ready") return false;
    const effectiveStatus = order.status === "scheduled" ? "pending" : order.status;
    const idx = ["pending", "preparing", "ready"].indexOf(effectiveStatus);
    return idx < 2;
  };

  const canGoBack = (order) => {
    return ["preparing", "ready"].includes(order.status);
  };

  const getNextStatus = (currentStatus) => {
    const flow = ["pending", "preparing", "ready"];
    const idx = flow.indexOf(currentStatus);
    return idx < flow.length - 1 ? flow[idx + 1] : null;
  };

  const getPrevStatus = (currentStatus) => {
    const flow = ["pending", "preparing", "ready"];
    const idx = flow.indexOf(currentStatus);
    return idx > 0 ? flow[idx - 1] : null;
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="kds-loading">
      <div className="flex flex-col items-center gap-3">
        <ChefHat className="w-10 h-10 text-emerald-600 animate-pulse" />
        <p className="text-sm text-gray-500">Carregando cozinha...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kds-page">
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between shadow-sm" data-testid="kds-header">
        <div className="flex items-center gap-3">
          <ChefHat className="w-6 h-6 text-emerald-600" />
          <h1 className="text-lg font-bold text-gray-800 uppercase tracking-wide">Monitor de Cozinha</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <div className="text-center">
              <span className="font-bold text-gray-900 text-lg">{orders.length}</span>
              <span className="ml-1">ativos</span>
            </div>
            <span className="text-gray-300">|</span>
            <span>{lastUpdate.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => { const next = !soundEnabled; soundEnabledRef.current = next; setSoundEnabled(next); }} data-testid="toggle-sound">
            {soundEnabled ? <Volume2 className="w-5 h-5 text-gray-600" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchOrders} data-testid="refresh-kds">
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </Button>
          <div className={`w-2.5 h-2.5 rounded-full ${wsConnected ? "bg-emerald-500" : "bg-red-400"}`} title={wsConnected ? "Tiempo real activo" : "Reconectando..."} data-testid="ws-status" />
        </div>
      </div>

      <div className="flex h-[calc(100vh-57px)]" data-testid="kds-kanban">
        {KITCHEN_COLUMNS.map(col => {
          const colOrders = sortActiveOrdersByUrgency(orders.filter(o =>
            o.status === col.key || (col.key === "pending" && o.status === "scheduled")
          ));

          return (
            <div key={col.key} className="flex-1 border-r border-gray-200 flex flex-col bg-white" data-testid={`kds-column-${col.key}`}>
              <div className={`px-4 py-3 flex items-center justify-between ${col.key === "pending" ? "bg-amber-50" : col.key === "preparing" ? "bg-blue-50" : "bg-emerald-50"}`}>
                <div className="flex items-center gap-2">
                  <col.icon className={`w-5 h-5 ${col.key === "pending" ? "text-amber-600" : col.key === "preparing" ? "text-blue-600" : "text-emerald-600"}`} />
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{col.label}</h2>
                  {colOrders.length > 0 && (
                    <span className={`${col.color} text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center`}>
                      {colOrders.length}
                    </span>
                  )}
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-3 space-y-3">
                  {colOrders.map(order => {
                    const type = getOrderType(order);
                    const isDelivery = order.delivery_address || order.source === "delivery";
                    const next = canAdvance(order, col.key) ? getNextStatus(col.key) : null;
                    const prev = canGoBack(order) ? getPrevStatus(col.key) : null;

                    const isScheduled = !!order.scheduled_for;
                    const elapsedFromScheduled = isScheduled
                      ? Math.floor((currentTime.getTime() - new Date(order.scheduled_for).getTime()) / 60000)
                      : 0;
                    const elapsed = isScheduled
                      ? Math.max(0, elapsedFromScheduled)
                      : getElapsed(order.created_at);

                    const scheduledTimeStr = isScheduled
                      ? new Date(order.scheduled_for).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
                      : null;

                    const scheduledDateStr = isScheduled ? formatScheduledDateForKDS(order.scheduled_for) : null;

                    const isAwaitingPayment = order.status === "awaiting_payment";

                    return (
                      <div
                        key={order.id}
                        className={`rounded-lg border-2 bg-white overflow-hidden shadow-sm ${
                          isAwaitingPayment ? "border-amber-400 opacity-75" :
                          isScheduled ? "border-blue-400" : elapsed > 30 ? "border-red-400" : elapsed > 15 ? "border-amber-400" : "border-gray-200"
                        }`}
                        data-testid={`kds-card-${order.id.slice(-6)}`}
                      >
                        {isAwaitingPayment && (
                          <div className="bg-amber-400 text-amber-900 px-3 py-1 flex items-center gap-1.5 text-xs font-bold">
                            <span>⏳</span>
                            AGUARDANDO PAGAMENTO — Não iniciar
                          </div>
                        )}
                        {isScheduled && (
                          <div className="bg-blue-500 text-white px-3 py-1 flex items-center gap-1.5 text-xs font-bold">
                            <CalendarClock className="w-3.5 h-3.5" />
                            AGENDADO {scheduledDateStr} para as {scheduledTimeStr}
                            {elapsedFromScheduled < 0 && (
                              <span className="ml-auto font-normal opacity-90">em {Math.abs(elapsedFromScheduled)}m</span>
                            )}
                          </div>
                        )}
                        <div className="px-3 py-2 flex items-center justify-between bg-gray-50 border-b">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-gray-900">#{order.id.slice(-6).toUpperCase()}</span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${type.badge}`}>
                              {type.label}
                            </span>
                            {order.table_number && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${_isBalcao(order.table_name) ? "text-yellow-700 bg-yellow-50 border-yellow-200" : "text-orange-700 bg-orange-50 border-orange-200"}`}>
                                {order.table_name || `Mesa ${order.table_number}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {order.status === "ready" && (() => {
                              const updatedAt = new Date(order.updated_at || order.created_at);
                              const minsInReady = Math.floor((currentTime - updatedAt) / 60000);
                              const minsLeft = LISTO_AUTO_HIDE_MINS - minsInReady;
                              if (minsLeft <= 5 && minsLeft > 0) {
                                return (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                                    oculta em {minsLeft}m
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              isScheduled && elapsedFromScheduled < 0
                                ? "bg-blue-100 text-blue-700"
                                : elapsed > 30 ? "bg-red-100 text-red-700"
                                : elapsed > 15 ? "bg-amber-100 text-amber-700"
                                : "bg-gray-100 text-gray-600"
                            }`}>
                              {isScheduled && elapsedFromScheduled < 0 ? `-${Math.abs(elapsedFromScheduled)}m` : `${elapsed}m`}
                            </span>
                            <button
                              className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:bg-red-100 hover:text-red-600 transition"
                              onClick={() => cancelOrder(order.id, order.id.slice(-6).toUpperCase())}
                              title="Cancelar pedido"
                              data-testid={`kds-cancel-${order.id.slice(-6)}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="px-3 pt-2 flex items-center gap-2 text-xs text-gray-500">
                          <div className="w-8 h-8 rounded-full overflow-hidden bg-emerald-100 flex items-center justify-center shrink-0">
                            {order.customer_avatar_url
                              ? <img src={order.customer_avatar_url} alt="" className="w-full h-full object-cover" />
                              : <span className="text-xs font-black text-emerald-700">{(order.customer_name || "?").slice(0, 1).toUpperCase()}</span>}
                          </div>
                          <div className="min-w-0"><User className="w-3 h-3 inline mr-1" /><span className="font-medium">{order.customer_name || "Sin nombre"}</span></div>
                        </div>

                        <div className="px-3 py-2 space-y-1">
                          {order.items.map((item, idx) => {
                            const rawName = item.product_name || item.name || '';
                            const packUnits = item.units_per_pack || (() => {
                              const m = rawName.match(/\((\d+)\s*unidades?\)/i);
                              return m ? parseInt(m[1], 10) : null;
                            })();
                            const isPackUnitsMeta = Boolean(
                              packUnits && /\(\s*\d+\s*unidades?\s*\)/i.test(rawName),
                            );
                            const variations = item.selected_variations?.length > 0
                              ? item.selected_variations
                              : (() => {
                                  if (isPackUnitsMeta) return [];
                                  const mods = (item.modifiers || []).flatMap(m =>
                                    (m.options || []).map(o => ({ name: o.name, quantity: o.quantity || 1 }))
                                  ).filter(v => v.name);
                                  if (mods.length > 0) return mods;
                                  const match = rawName.match(/\(([^)]+)\)$/);
                                  return match ? match[1].split(', ').map(v => ({ name: v.trim(), quantity: 1 })) : [];
                                })();
                            const baseName = isPackUnitsMeta
                              ? rawName.replace(/\s*\(\d+\s*unidades?\)/i, "").trim()
                              : (variations.length > 0 ? rawName.replace(/\s*\([^)]+\)$/, "") : rawName);
                            const badgeQty = item.commercial_qty ?? item.quantity;
                            return (
                              <div key={idx} className="flex items-start gap-2">
                                <span className="font-bold text-sm text-emerald-700 bg-emerald-50 rounded w-6 h-6 flex items-center justify-center flex-shrink-0">{badgeQty}</span>
                                <div className="flex-1">
                                  <span className="text-sm text-gray-800 font-medium">{baseName}</span>
                                  {packUnits && (
                                    <p className="text-xs text-gray-500 mt-0.5">{packUnits} unidades</p>
                                  )}
                                  {variations.length > 0 && (
                                    <div className="mt-0.5 pl-2 border-l-2 border-emerald-300 space-y-0">
                                      {variations.map((v, vi) => (
                                        <div key={vi} className="flex items-center gap-1">
                                          <span className="text-xs font-bold text-emerald-700 w-4 text-right shrink-0">{v.quantity || 1}×</span>
                                          <span className="text-xs text-gray-700">{v.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {item.notes && (
                                    <p className="text-xs text-red-600 font-medium mt-0.5">* {item.notes}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {order.notes && (
                            <p className="text-xs text-orange-600 font-medium bg-orange-50 p-2 rounded mt-2">OBS: {order.notes}</p>
                          )}
                        </div>

                        {isDelivery && col.key === "ready" && (
                          <div className="px-3 pb-2">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700">
                              <div className="flex items-center gap-1 font-semibold mb-1">
                                <Truck className="w-3 h-3" /> Aguardando estafeta
                              </div>
                              {order.delivery_address && <p className="text-blue-600">{order.delivery_address}</p>}
                            </div>
                          </div>
                        )}

                        <div className="px-3 py-2 border-t flex items-center justify-between gap-2">
                          {prev ? (
                            <button
                              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition"
                              onClick={() => updateStatus(order.id, prev)}
                              data-testid={`kds-prev-${order.id.slice(-6)}`}
                            >
                              <ChevronLeft className="w-4 h-4 inline" /> Voltar
                            </button>
                          ) : <div />}

                          {next ? (
                            <button
                              className={`px-4 py-2 rounded-lg text-xs font-bold text-white transition shadow-sm ${
                                next === "preparing" ? "bg-blue-500 hover:bg-blue-600" : "bg-emerald-500 hover:bg-emerald-600"
                              }`}
                              onClick={() => updateStatus(order.id, next)}
                              data-testid={`kds-next-${order.id.slice(-6)}`}
                            >
                              {next === "preparing" ? "Iniciar" : "Pronto"} <ChevronRight className="w-4 h-4 inline" />
                            </button>
                          ) : (
                            isDelivery && col.key === "ready" ? (
                              <span className="text-xs text-blue-600 font-medium italic">Atendente envia</span>
                            ) : !isDelivery && col.key === "ready" ? (
                              <button
                                className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-green-600 hover:bg-green-700 transition shadow-sm"
                                onClick={() => updateStatus(order.id, "delivered")}
                                data-testid={`kds-deliver-${order.id.slice(-6)}`}
                              >
                                {order.table_number && !_isBalcao(order.table_name) ? "Serviu ✓" : "Finalizar ✓"}
                              </button>
                            ) : null
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {colOrders.length === 0 && (
                    <div className="text-center py-16 text-gray-300">
                      <col.icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-medium">Sin pedidos</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          );
        })}
      </div>
    </div>
  );
}
